/**
 * SiteGPT Widget - Entry Point
 *
 * Embeddable chatbot widget for SiteGPT
 *
 * Usage:
 * <script src="https://widget.site-gpt.com/v1/widget.js"
 *         data-sitegpt-bot-id="YOUR_BOT_ID"></script>
 *
 * Or programmatic initialization:
 * window.SiteGPT.init({ botId: 'YOUR_BOT_ID' });
 */

import { Widget } from './widget';
import type { SiteGPTWidgetConfig, SiteGPTAPI } from './types/config';

declare const __VERSION__: string;

let widgetInstance: Widget | null = null;

/**
 * SiteGPT API exposed on window
 */
const SiteGPT: SiteGPTAPI = {
  version: typeof __VERSION__ !== 'undefined' ? __VERSION__ : '1.0.0',

  /**
   * Initialize the widget with configuration
   */
  init(config: SiteGPTWidgetConfig): void {
    if (widgetInstance) {
      console.warn('[SiteGPT] Widget already initialized. Call destroy() first to reinitialize.');
      return;
    }

    if (!config.botId) {
      console.error('[SiteGPT] botId is required');
      return;
    }

    widgetInstance = new Widget(config);
    widgetInstance.mount();
  },

  /**
   * Open the chat window
   */
  open(): void {
    if (!widgetInstance) {
      console.warn('[SiteGPT] Widget not initialized. Call init() first.');
      return;
    }
    widgetInstance.open();
  },

  /**
   * Close the chat window
   */
  close(): void {
    if (!widgetInstance) {
      console.warn('[SiteGPT] Widget not initialized. Call init() first.');
      return;
    }
    widgetInstance.close();
  },

  /**
   * Toggle the chat window
   */
  toggle(): void {
    if (!widgetInstance) {
      console.warn('[SiteGPT] Widget not initialized. Call init() first.');
      return;
    }
    widgetInstance.toggle();
  },

  /**
   * Destroy the widget and remove from DOM
   */
  destroy(): void {
    if (!widgetInstance) {
      return;
    }
    widgetInstance.unmount();
    widgetInstance = null;
  },

  /**
   * Check if widget is initialized
   */
  isInitialized(): boolean {
    return widgetInstance !== null;
  },

  /**
   * Reset the conversation - clears all messages and starts fresh
   */
  resetConversation(): void {
    if (!widgetInstance) {
      console.warn('[SiteGPT] Widget not initialized. Call init() first.');
      return;
    }
    widgetInstance.resetConversation();
  },
};

// Expose globally
if (typeof window !== 'undefined') {
  window.SiteGPT = SiteGPT;
}

/**
 * Auto-initialize from script data attributes
 */
function autoInit(): void {
  // Find the script tag with our attributes
  const scripts = document.querySelectorAll('script[data-sitegpt-bot-id]');

  if (scripts.length === 0) {
    return;
  }

  // Use the last script tag (in case of duplicates)
  const script = scripts[scripts.length - 1] as HTMLScriptElement;

  const botId = script.getAttribute('data-sitegpt-bot-id');
  if (!botId) {
    return;
  }

  // Parse configuration from data attributes
  const config: SiteGPTWidgetConfig = {
    botId,
  };

  // Optional attributes
  const position = script.getAttribute('data-sitegpt-position');
  if (position === 'bottom-left' || position === 'bottom-right') {
    config.position = position;
  }

  const primaryColor = script.getAttribute('data-sitegpt-color');
  if (primaryColor) {
    config.primaryColor = primaryColor;
  }

  const welcomeMessage = script.getAttribute('data-sitegpt-welcome');
  if (welcomeMessage) {
    config.welcomeMessage = welcomeMessage;
  }

  const openOnLoad = script.getAttribute('data-sitegpt-open');
  if (openOnLoad === 'true') {
    config.openOnLoad = true;
  }

  const showLeadForm = script.getAttribute('data-sitegpt-lead-form');
  if (showLeadForm === 'false') {
    config.showLeadForm = false;
  }

  const apiBaseUrl = script.getAttribute('data-sitegpt-api-url');
  if (apiBaseUrl) {
    config.apiBaseUrl = apiBaseUrl;
  }

  const debug = script.getAttribute('data-sitegpt-debug');
  if (debug === 'true') {
    config.debug = true;
  }

  const disableWelcomeCloud = script.getAttribute('data-sitegpt-disable-welcome-cloud');
  if (disableWelcomeCloud === 'true') {
    config.disableWelcomeCloud = true;
  }

  // Initialize
  SiteGPT.init(config);
}

// Run auto-init when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    // DOM already loaded
    autoInit();
  }
}

// Export types for TypeScript users
export type { SiteGPTWidgetConfig, SiteGPTAPI } from './types/config';
export { SiteGPT };
